import React, { useState, useEffect, useMemo } from 'react';
import { Percent, RefreshCw, Calculator } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт утилит и функций
import { convertPercentage } from '../../../utils/calculations/finance';

// Импорт компонентов
import CalculationForm from './PercentageConverterPage/components/CalculationForm';
import ResultsOverview from './PercentageConverterPage/components/ResultsOverview';
import QuickAdjustmentSidebar from './PercentageConverterPage/components/QuickAdjustmentSidebar';
import TipsSidebar from './PercentageConverterPage/components/TipsSidebar';
import InfoContent from './PercentageConverterPage/components/InfoContent';

// Начальные данные
export const initialFormData = {
  value: '10',
  fromType: 'percent',
  toType: 'decimal',
};

const PercentageConverterPage = () => {
  const { darkMode, toggleDarkMode } = useTheme();

  // Данные инструмента
  const tool = useMemo(() => getToolById('percentage-converter'), []);
  const pageTitle = tool?.metaTitle || 'Конвертер процентов | Gensio';
  const metaDescription = tool?.metaDescription || 'Онлайн конвертер процентов: преобразование значений из процентов в десятичные дроби, проценты, обыкновенные дроби и обратно.';

  // Состояния
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);

  // Обновление заголовка и мета-описания
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // Функция расчета конвертации
  const calculateConversion = () => {
    setIsCalculating(true);
    try {
      const convertedValue = convertPercentage(
        parseFloat(formData.value), 
        formData.fromType, 
        formData.toType
      );

      setResults({
        originalValue: formData.value,
        originalType: formData.fromType,
        convertedValue,
        convertedType: formData.toType
      });
      setErrors({});
    } catch (error) {
      setErrors({ general: error.message });
      setResults(null);
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* Основной контент */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Заголовок страницы */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Percent className="w-8 h-8 text-green-600 dark:text-green-400 flex-shrink-0" />
                {tool?.name || 'Конвертер процентов'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* Форма конвертера */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              calculateConversion={calculateConversion}
              isCalculating={isCalculating}
              errors={errors}
            />

            {/* Результаты конвертации */}
            {results && !isCalculating && (
              <ResultsOverview 
                results={results}
              />
            )}

            {/* Информационный контент */}
            <InfoContent />
          </div>

          {/* Боковая панель */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* Быстрая настройка */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              calculateConversion={calculateConversion}
            />

            {/* Полезные советы */}
            <TipsSidebar />

            {/* Связанные инструменты */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PercentageConverterPage;